<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      <p class="provider-page__subtitle">{{ headerSubtitle() }}</p>
    </div>
    <div class="buttons">
      <p-button [label]="'Business' | translateWF: 'Business'" icon="pi pi-arrow-left" variant="outlined" (click)="back()" />
      <span class="separator"></span>
      @if (canEdit()) {
        <p-button [label]="'Save' | translateWF: 'Save'" icon="pi pi-save" (click)="save()" />
      }
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <!-- The master-data dropdowns raise their delete confirmation on key "ct". -->
    <p-confirmDialog key="ct" />

    <!-- Nothing renders until the business is in hand, and not merely to avoid an empty flash: the children
         read their edit/read-only state ONCE, in ngOnInit. app-financial-account-select in particular only
         fetches the chart of accounts when it initializes editable — mounted during the load, when canEdit()
         is still false, it would come up read-only and the AP Control Account picker would stay empty for
         good, since it does not watch that input for changes. -->
    @if (business()) {
      @if (!isProvider()) {
        <!-- The role is granted on the business form. Without it there is no provider block to edit, and the
             API would refuse the save anyway — so point back rather than present a form that cannot be kept. -->
        <p-card>
          <p-message
            severity="warn"
            styleClass="w-full"
            [text]="'Provider_Details_NotAProvider' | translateWF: 'This business does not hold the Provider role. Assign it on the business form to set up provider details.'"
          />
        </p-card>

        <div class="mt-3">
          <p-button [label]="'Business' | translateWF: 'Business'" icon="pi pi-arrow-left" variant="outlined" (click)="back()" />
        </div>
      } @else {
        <div class="grid-12">
          <!-- Section rail -->
          <div class="span-12-3-2">
            <div class="provider-page__rail">
              <span class="provider-page__rail-title">{{ 'Sections' | translateWF: 'Sections' }}</span>
              <p-panelmenu [model]="sectionMenu()" styleClass="w-full cs-nav-rail" />
            </div>
          </div>

          <!-- Sections -->
          <div class="col span-12-9-10">
            <form [formGroup]="providerForm" class="col">
              <!-- Classification — hidden for now (HIDDEN_SECTIONS); the controls stay bound so the stored
                 values keep round-tripping through save untouched. -->
              @if (isSectionVisible('classification')) {
                <p-panel id="classification">
                  <ng-template pTemplate="header">
                    <div class="provider-page__section-header">
                      <i class="pi pi-tags"></i>
                      <span>{{ 'Provider_Section_Classification' | translateWF: 'Classification' }}</span>
                    </div>
                  </ng-template>

                  <p-card>
                    <div class="grid-12">
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Provider Type Code' | translateWF: 'Provider Type Code' }}</label>
                          <csl-dropdown-master-data
                            class="w-full"
                            formControlName="providerTypeCode"
                            categoryCode="ProviderType"
                            [allowCreate]="true"
                            (onDelete)="deleteItemMasterData($event)"
                          />
                        </div>
                      </div>
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Provider_CommercialName' | translateWF: 'Commercial Name' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="commercialName" />
                        </div>
                      </div>
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Account Group Code' | translateWF: 'Account Group Code' }}</label>
                          <csl-dropdown-master-data
                            class="w-full"
                            categoryCode="AccountGroup"
                            formControlName="accountGroupCode"
                            [allowCreate]="true"
                            (onDelete)="deleteItemMasterData($event)"
                          />
                        </div>
                      </div>
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Provider Block Reason Code' | translateWF: 'Provider Block Reason Code' }}</label>
                          <csl-dropdown-master-data
                            class="w-full"
                            categoryCode="ProviderBlock"
                            formControlName="providerBlockReasonCode"
                            [allowCreate]="true"
                            (onDelete)="deleteItemMasterData($event)"
                          />
                        </div>
                      </div>
                      <div class="span-12">
                        <span class="provider-page__subhead">{{ 'Provider_OneTimeVendor' | translateWF: 'One-Time Vendor' }}</span>
                        <div class="row-sm-start-center mt-2">
                          <p-checkbox formControlName="isOneTimeVendor" [binary]="true" inputId="isOneTimeVendor" />
                          <label for="isOneTimeVendor" class="cursor-pointer text-sm">
                            {{ 'Provider_OneTimeVendor_Hint' | translateWF: 'Occasional / non-recurring provider' }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </p-card>
                </p-panel>
              }

              <!-- Purchasing — hidden for now, same as Classification above. -->
              @if (isSectionVisible('purchasing')) {
                <p-panel id="purchasing">
                  <ng-template pTemplate="header">
                    <div class="provider-page__section-header">
                      <i class="pi pi-shopping-cart"></i>
                      <span>{{ 'Provider_Section_Purchasing' | translateWF: 'Purchasing' }}</span>
                    </div>
                  </ng-template>

                  <p-card>
                    <div class="grid-12">
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Purchase Organization Code' | translateWF: 'Purchase Organization Code' }}</label>
                          <csl-dropdown-master-data
                            class="w-full"
                            formControlName="purchaseOrganizationCode"
                            categoryCode="PurchaseOrganization"
                            [allowCreate]="true"
                            (onDelete)="deleteItemMasterData($event)"
                            (emitMessagesApi)="showMessagesApi($event, 'Item Delete successfully.')"
                          />
                        </div>
                      </div>
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Currency Code' | translateWF: 'Currency Code' }}</label>
                          <csl-dropdown-master-data
                            class="w-full"
                            categoryCode="Currency"
                            formControlName="currencyCode"
                            [allowCreate]="true"
                            (onDelete)="deleteItemMasterData($event)"
                          />
                        </div>
                      </div>
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Lead Associate' | translateWF: 'Lead Associate' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="leadAssociateUserName" />
                        </div>
                      </div>
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Last Purchase Order Date' | translateWF: 'Last Purchase Order Date' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="lastPurchaseOrderDate" />
                        </div>
                      </div>
                    </div>
                  </p-card>
                </p-panel>
              }

              <!-- Payment & Accounts Payable -->
              <p-panel id="payment">
                <ng-template pTemplate="header">
                  <div class="provider-page__section-header">
                    <i class="pi pi-credit-card"></i>
                    <span>{{ 'Provider_Section_Payment' | translateWF: 'Payment & Accounts Payable' }}</span>
                  </div>
                </ng-template>

                <p-card>
                  <!-- One grid per row rather than one grid for the section: the account field is half-width,
                       so a single auto-flowing grid would pull the credit terms up beside it instead of
                       starting the row the layout calls for. -->
                  <div class="grid-12">
                    <div class="span-12-6">
                      <div class="form-box">
                        <app-financial-account-select
                          [label]="'Provider_ApGlAccount' | translateWF: 'AP Control Account'"
                          [referenceControl]="apReferenceControl"
                          [numberControl]="apNumberControl"
                          [nameControl]="apAccountNameControl"
                          [postingOnly]="true"
                          [readonly]="!canEdit()"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="grid-12 mt-5">
                    <div class="span-12-4">
                      <div class="form-box">
                        <label>{{ 'Payment Terms Code' | translateWF: 'Payment Terms Code' }}</label>
                        <csl-dropdown-master-data
                          class="w-full"
                          categoryCode="PaymentTermsProvider"
                          formControlName="paymentTermsCode"
                          [allowCreate]="true"
                          (onDelete)="deleteItemMasterData($event)"
                        />
                      </div>
                    </div>
                    <div class="span-12-4">
                      <div class="form-box">
                        <!-- The "derived from payment terms" note is an icon, not trailing text: as a second
                             line it made this label taller than its neighbours and knocked the whole row's
                             inputs out of alignment. -->
                        <label class="row-start-center gap-1">
                          {{ 'Provider_CreditDays' | translateWF: 'Credit Days' }}
                          <i
                            class="pi pi-info-circle text-xs text-surface-400"
                            [pTooltip]="'Provider_CreditDays_Auto' | translateWF: 'Set automatically from the payment terms.'"
                            tooltipPosition="top"
                          ></i>
                        </label>
                        <p-inputNumber formControlName="creditDays" [min]="0" [useGrouping]="false" styleClass="w-full" />
                      </div>
                    </div>
                    <div class="span-12-4">
                      <div class="form-box">
                        <label>{{ 'Provider_CreditLimit' | translateWF: 'Credit Limit (USD)' }}</label>
                        <!-- currencyDisplay="code" renders "USD 50,000.00" — an unambiguous denomination even
                             if a translation overrides the label; "$" alone could read as Lempira. -->
                        <p-inputNumber formControlName="creditLimit" [min]="0" mode="currency" currency="USD" currencyDisplay="code" styleClass="w-full" />
                      </div>
                    </div>
                  </div>

                  <div class="grid-12 mt-5">
                    <div class="span-12-4">
                      <div class="form-box">
                        <label>{{ 'Provider_PaymentMethod' | translateWF: 'Payment Method' }}</label>
                        <csl-dropdown-master-data
                          class="w-full"
                          categoryCode="PaymentMethod"
                          formControlName="paymentMethodCode"
                          [allowCreate]="true"
                          (onDelete)="deleteItemMasterData($event)"
                        />
                      </div>
                    </div>
                  </div>
                </p-card>
              </p-panel>

              <!-- Payable Bank Accounts -->
              <p-panel id="banks" formArrayName="banks">
                <ng-template pTemplate="header">
                  <div class="provider-page__section-header">
                    <i class="pi pi-building-columns"></i>
                    <span>{{ 'Provider_Section_Banking' | translateWF: 'Payable Bank Accounts' }}</span>
                  </div>
                </ng-template>
                <ng-template pTemplate="icons">
                  @if (canEdit()) {
                    <p-button [label]="'Provider_AddBank' | translateWF: 'Add bank'" icon="pi pi-plus" size="small" variant="outlined" (click)="addBank()" />
                  }
                </ng-template>

                <p-card>
                  @if (banks.controls.length === 0) {
                    <p class="text-sm text-surface-400">
                      {{
                        'Provider_NoBanks'
                          | translateWF
                            : 'No bank accounts. The AP payment request picks a destination
                from this list.'
                      }}
                    </p>
                  }

                  @for (bank of banks.controls; track bank; let i = $index) {
                    <div class="provider-page__bank grid-12" [formGroupName]="i">
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_Alias' | translateWF: 'Alias' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="alias" [placeholder]="'Provider_Bank_Alias_Ph' | translateWF: 'e.g. BAC · USD'" />
                        </div>
                      </div>
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_Name' | translateWF: 'Bank Name' }} <span class="text-red-500">*</span></label>
                          <input
                            type="text"
                            pInputText
                            class="w-full"
                            formControlName="bankName"
                            [ngClass]="{ 'ng-invalid ng-dirty': bank.controls.bankName.invalid && bank.controls.bankName.touched }"
                          />
                        </div>
                      </div>
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_AccountNumber' | translateWF: 'Account Number' }} <span class="text-red-500">*</span></label>
                          <input
                            type="text"
                            pInputText
                            class="w-full"
                            formControlName="accountNumber"
                            [ngClass]="{ 'ng-invalid ng-dirty': bank.controls.accountNumber.invalid && bank.controls.accountNumber.touched }"
                          />
                        </div>
                      </div>
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_Currency' | translateWF: 'Currency' }} <span class="text-red-500">*</span></label>
                          <p-select
                            [options]="currencyOptions()"
                            formControlName="currency"
                            optionLabel="label"
                            optionValue="value"
                            [showClear]="true"
                            appendTo="body"
                            styleClass="w-full"
                            [ngClass]="{ 'ng-invalid ng-dirty': bank.controls.currency.invalid && bank.controls.currency.touched }"
                          />
                        </div>
                      </div>
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_Type' | translateWF: 'Bank Type' }}</label>
                          <p-select [options]="bankTypeOptions" formControlName="bankType" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full" />
                          <small class="text-surface-500">{{
                            'Provider_Bank_Type_Hint'
                              | translateWF
                                : 'How the payment is
                      routed.'
                          }}</small>
                        </div>
                      </div>
                      <!-- What the bank calls the account, which is not the same question as the routing
                           role above — hence both labels say which they mean. -->
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_AccountType' | translateWF: 'Account Type' }} <span class="text-red-500">*</span></label>
                          <p-select
                            [options]="accountTypeOptions"
                            formControlName="accountType"
                            optionLabel="label"
                            optionValue="value"
                            appendTo="body"
                            styleClass="w-full"
                            [ngClass]="{ 'ng-invalid ng-dirty': bank.controls.accountType.invalid && bank.controls.accountType.touched }"
                          />
                        </div>
                      </div>
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_Swift' | translateWF: 'SWIFT / BIC' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="swiftBicCode" />
                        </div>
                      </div>
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_Iban' | translateWF: 'IBAN' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="ibanCode" />
                        </div>
                      </div>
                      <div class="span-12-3">
                        <div class="form-box">
                          <label>{{ 'Provider_Bank_Address' | translateWF: 'Bank Address' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="bankAddress" />
                        </div>
                      </div>
                      <div class="span-12 row-sm-between-center">
                        <div class="row-sm-start-center">
                          <p-checkbox formControlName="isPrimary" [binary]="true" [inputId]="'bankPrimary' + i" />
                          <label [for]="'bankPrimary' + i" class="cursor-pointer text-sm">{{ 'Provider_Bank_Primary' | translateWF: 'Primary' }}</label>
                        </div>
                        @if (canEdit()) {
                          <p-button icon="pi pi-trash" severity="danger" variant="text" (click)="removeBank(i)" />
                        }
                      </div>
                    </div>
                  }
                </p-card>
              </p-panel>

              <!-- Tax & Fiscal -->
              <p-panel id="tax">
                <ng-template pTemplate="header">
                  <div class="provider-page__section-header">
                    <i class="pi pi-percentage"></i>
                    <span>{{ 'Provider_Section_Tax' | translateWF: 'Tax & Fiscal' }}</span>
                  </div>
                </ng-template>

                <p-card>
                  <div class="grid-12">
                    <!-- Tax Status / Regime, SGI and CIS are hidden for now (HIDDEN_FIELDS); the controls
                         stay bound so the stored values keep round-tripping through save untouched. -->
                    @if (isFieldVisible('taxRegimeCode')) {
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'Provider_TaxRegime' | translateWF: 'Tax Status / Regime' }}</label>
                          <csl-dropdown-master-data
                            class="w-full"
                            categoryCode="TaxRegime"
                            formControlName="taxRegimeCode"
                            [allowCreate]="true"
                            (onDelete)="deleteItemMasterData($event)"
                          />
                        </div>
                      </div>
                    }
                    @if (isFieldVisible('sgiCode')) {
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'SGI Code' | translateWF: 'SGI Code' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="sgiCode" />
                        </div>
                      </div>
                    }
                    @if (isFieldVisible('cisCode')) {
                      <div class="span-12-6-3">
                        <div class="form-box">
                          <label>{{ 'CIS Code' | translateWF: 'CIS Code' }}</label>
                          <input type="text" pInputText class="w-full" formControlName="cisCode" />
                        </div>
                      </div>
                    }
                    <!-- Shown, not edited. The country is the business's (#5121) — one fact about the
                         company, the same whether it sells to us or buys from us. It drives the fiscal
                         (CAI) requirement; the withholding jurisdiction next to it is fixed and does NOT
                         follow it. -->
                    <div class="span-12-6-3">
                      <div class="form-box">
                        <label>{{ 'Provider_Country' | translateWF: 'Domicile Country' }}</label>
                        <div class="flex h-[2.5rem] items-center font-medium">
                          {{ countryCode() || ('Provider_Country_Unset' | translateWF: 'Not set') }}
                        </div>
                        <small class="text-surface-500">{{
                          'Provider_Country_Hint'
                            | translateWF
                              : 'From the business record — edit it there. Drives the
                      fiscal (CAI) requirement.'
                        }}</small>
                      </div>
                    </div>
                    <!-- Fixed jurisdiction: we only withhold Honduran taxes, whatever the domicile — a USA or
                         GTM provider is still withheld under HND. Non-editable until multi-country launches. -->
                    <div class="span-12-6-3">
                      <div class="form-box">
                        <label>{{ 'Provider_WithholdingCountry' | translateWF: 'Withholding Country' }}</label>
                        <div class="flex h-[2.5rem] items-center font-medium">
                          {{ withholdingCountry }}
                        </div>
                        <small class="text-surface-500">{{
                          'Provider_WithholdingCountry_Hint'
                            | translateWF
                              : 'Fixed — only Honduran taxes are withheld,
                      regardless of the domicile. Scopes the codes below.'
                        }}</small>
                      </div>
                    </div>
                    <div class="span-12-6-3">
                      <div class="form-box">
                        <label>{{ 'Provider_WithholdingResidency' | translateWF: 'Withholding Residency' }}</label>
                        <p-select
                          [options]="residencyOptions"
                          formControlName="withholdingResidency"
                          optionLabel="label"
                          optionValue="value"
                          [showClear]="true"
                          appendTo="body"
                          styleClass="w-full"
                          [placeholder]="'Provider_WithholdingResidency_Ph' | translateWF: 'Select residency'"
                        />
                        <small class="text-surface-500">{{
                          'Provider_WithholdingResidency_Hint'
                            | translateWF
                              : 'Resident /
                      Non-resident — read-only on AP documents.'
                        }}</small>
                      </div>
                    </div>
                    <div class="span-12-6-3">
                      <div class="form-box">
                        <label>{{ 'Provider_DefaultWithholding' | translateWF: 'Default Withholding' }}</label>
                        <p-select
                          [options]="withholdingCodeOptions()"
                          formControlName="defaultWithholdingCode"
                          optionLabel="label"
                          optionValue="value"
                          [showClear]="true"
                          [filter]="true"
                          appendTo="body"
                          styleClass="w-full"
                          [placeholder]="'Provider_DefaultWithholding_Ph' | translateWF: 'Select a default code'"
                        />
                        <small class="text-surface-500">{{
                          'Provider_DefaultWithholding_Hint'
                            | translateWF
                              : 'Pre-selected
                      on AP documents; overridable per line.'
                        }}</small>
                      </div>
                    </div>
                  </div>
                </p-card>
              </p-panel>
            </form>

            <!-- Tax Authorizations — outside the form on purpose: each row is written the moment its modal
               closes, so Save above does not (and must not appear to) cover this section. -->
            <p-card>
              <div class="provider-page__section-header mb-2">
                <i class="pi pi-verified"></i>
                <p-tag severity="info">
                  <i class="pi pi-bolt mr-1"></i
                  >{{
                    'Provider_SavedOnItsOwn'
                      | translateWF
                        : 'Saved
              on
              its own'
                  }}
                </p-tag>
              </div>
              <p class="text-sm text-[var(--p-text-muted-color)] mb-3">
                {{
                  'Provider_TaxAuth_Note'
                    | translateWF
                      : 'Each authorization is added, edited or revoked immediately. The Save button at the top of
            this page does not apply to this section.'
                }}
              </p>
            </p-card>
            <!-- The viewer brings its own titled panel, so this section only adds the provider-specific
                 framing: that these rows save immediately, unlike the form above. -->
            <app-tax-authorizations-viewer [businessId]="currentId()" [businessName]="business()?.name ?? null" [viewOnly]="isReadOnly()" />
          </div>
        </div>
      }
    }
  </div>
</main>
